/**
 * ChapterHeroBatchModal — batch "Generate a hero for every missing chapter"
 * action for the Upload Draft flow (iter-22).
 *
 * Streams progress via the backend SSE endpoint
 *   POST /api/books/:id/chapters/hero-images/generate-all
 * which emits ``start`` → ``progress`` (many) → ``complete`` events. Every
 * ``progress`` event carries a per-chapter status transition
 * (generating → done | error). We render a progress bar + a per-chapter
 * status list so the user can watch the batch complete.
 *
 * Backend concurrency is capped at 3 to stay under Nano Banana rate limits;
 * this modal is a thin consumer that renders the stream.
 */
import React, { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, XCircle, Loader2, Sparkles, X, AlertCircle } from "lucide-react";
import { API, api } from "@/lib/api";

/** Per-chapter transient status derived from SSE events. */
function makeInitial(targets) {
  return targets.map((t) => ({
    source_index: t.source_index,
    title: t.title,
    status: "pending",
    error: null,
  }));
}

export function ChapterHeroBatchModal({ book, onClose, onBookUpdated }) {
  const [state, setState] = useState({ phase: "idle", rows: [], generated: 0, failed: 0, skipped: 0 });
  const [quotaHit, setQuotaHit] = useState(false);
  // iter-30: shared prompt template applied to every chapter. Supports
  // {chapter_title}, {chapter_subtitle}, {chapter_description} tokens.
  // Pre-fills with the book's last-used template so a user editing the
  // book later can tweak and re-run without retyping.
  const [promptTemplate, setPromptTemplate] = useState(
    book?.hero_batch_prompt_template || "");
  const [savingTemplate, setSavingTemplate] = useState(false);
  const abortRef = useRef(null);

  // Find chapters that qualify: real chapters (not part dividers) missing a
  // hero image. This drives the "will generate N" preview and the initial
  // per-chapter row list.
  const targets = (book?.chapters || [])
    .filter((c) => c._kind === "chapter"
                    && typeof c.chapter_source_index === "number"
                    && !c.hero_image)
    .map((c) => ({ source_index: c.chapter_source_index, title: c.title }));

  const start = useCallback(async () => {
    if (!targets.length) { toast.info("All chapters already have hero images."); return; }
    setState({ phase: "running", rows: makeInitial(targets),
               generated: 0, failed: 0, skipped: 0 });

    // Use fetch + ReadableStream because EventSource can't do POST or send
    // custom headers/cookies reliably. The backend responds with
    // ``text/event-stream`` framing.
    const controller = new AbortController();
    abortRef.current = controller;
    let response;
    try {
      response = await fetch(`${API}/books/${book.id}/chapters/hero-images/generate-all`, {
        method: "POST",
        credentials: "include",
        headers: {
          "Accept": "text/event-stream",
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          prompt_template: promptTemplate.trim() || null,
          persist_as_default: true,
        }),
        signal: controller.signal,
      });
    } catch (err) {
      if (err.name === "AbortError") return;
      toast.error(`Failed to start batch: ${err.message}`);
      setState((s) => ({ ...s, phase: "error" }));
      return;
    }
    if (!response.ok || !response.body) {
      toast.error(`Backend rejected batch (${response.status})`);
      setState((s) => ({ ...s, phase: "error" }));
      return;
    }
    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = "";
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      // Split on the SSE record delimiter.
      const chunks = buffer.split("\n\n");
      buffer = chunks.pop();
      for (const chunk of chunks) {
        const lines = chunk.split("\n");
        let event = "message";
        let data = "";
        for (const line of lines) {
          if (line.startsWith("event: ")) event = line.slice(7).trim();
          else if (line.startsWith("data: ")) data += line.slice(6);
        }
        if (!data) continue;
        let payload;
        try { payload = JSON.parse(data); } catch { continue; }
        if (event === "progress") {
          if (payload.code === "llm_quota_exceeded") {
            setQuotaHit(true);
          }
          setState((s) => ({
            ...s,
            rows: s.rows.map((r) => r.source_index === payload.source_index
              ? { ...r, status: payload.status, error: payload.error || null }
              : r),
          }));
        } else if (event === "complete") {
          setState((s) => ({ ...s, phase: "done",
                             generated: payload.generated ?? 0,
                             failed: payload.failed ?? 0,
                             skipped: payload.skipped ?? 0 }));
        } else if (event === "start") {
          // ignore — we prepped rows locally already.
        }
      }
    }
    abortRef.current = null;
    // Refresh the book document once at the end so hero thumbnails
    // materialise everywhere (structural editor, chapter preview, etc.).
    try {
      const { data } = await api.get(`/books/${book.id}`);
      onBookUpdated?.(data);
    } catch { /* non-fatal */ }
  }, [book.id, targets, onBookUpdated, promptTemplate]);

  const abort = useCallback(() => {
    abortRef.current?.abort();
    abortRef.current = null;
    setState((s) => ({ ...s, phase: "cancelled" }));
  }, []);

  // iter-30: persist the shared prompt template without running the batch.
  // Useful when every chapter already has a hero and the user just wants
  // to save a new template for the next regeneration.
  const savePromptTemplate = useCallback(async () => {
    setSavingTemplate(true);
    try {
      const { data } = await api.patch(`/books/${book.id}`, {
        hero_batch_prompt_template: promptTemplate.trim(),
      });
      onBookUpdated?.(data);
      toast.success("Prompt template saved");
      onClose();
    } catch (err) {
      toast.error("Failed to save prompt template");
    } finally { setSavingTemplate(false); }
  }, [book.id, promptTemplate, onBookUpdated, onClose]);

  useEffect(() => () => abortRef.current?.abort(), []);

  const doneCount = state.rows.filter((r) => r.status === "done").length;
  const errorCount = state.rows.filter((r) => r.status === "error").length;
  const runningCount = state.rows.filter((r) => r.status === "generating").length;
  const totalCount = state.rows.length || targets.length;
  const pct = totalCount ? Math.round(((doneCount + errorCount) / totalCount) * 100) : 0;
  const canClose = state.phase !== "running";

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-6"
         data-testid="hero-batch-modal"
         onClick={(e) => { if (e.target === e.currentTarget && canClose) onClose(); }}>
      <div className="bg-white border border-[color:var(--border)] shadow-2xl w-full max-w-2xl max-h-[85vh] flex flex-col">
        <div className="border-b border-[color:var(--border)] px-6 py-4 flex items-center justify-between">
          <div>
            <p className="text-xs uppercase tracking-[0.25em] text-[color:var(--text-muted)]">§ Batch hero generation</p>
            <h3 className="text-lg font-semibold mt-1">
              {state.phase === "idle"
                ? `Generate ${targets.length} hero image${targets.length === 1 ? "" : "s"}`
                : state.phase === "done"
                  ? "Batch complete"
                  : state.phase === "cancelled"
                    ? "Batch cancelled"
                    : `Generating… ${doneCount + errorCount} of ${totalCount}`}
            </h3>
            <p className="text-xs text-[color:var(--text-muted)] mt-1">
              {state.phase === "idle"
                ? "Chapters already with a hero are skipped. Uses Gemini Nano Banana · 3 concurrent · 10-30 s per chapter."
                : `${runningCount} in flight · ${doneCount} done · ${errorCount} failed`}
            </p>
          </div>
          <button
            onClick={() => canClose && onClose()}
            disabled={!canClose}
            className="btn-ghost text-sm inline-flex items-center gap-1"
            data-testid="hero-batch-close"
          >
            <X className="w-4 h-4"/> Close
          </button>
        </div>

        {/* iter-30: shared prompt template (applies to every chapter,
            variable-substituted per chapter). Editable both before the
            batch runs AND after a run completes, so the user can tweak
            and re-run for chapters that still need heroes. */}
        {(state.phase === "idle" || state.phase === "done" || state.phase === "cancelled") && (
          <div className="mx-6 mt-4">
            <label htmlFor="hero-batch-prompt" className="text-xs uppercase tracking-[0.25em] text-[color:var(--text-muted)] block">
              § Shared prompt template
            </label>
            <textarea
              id="hero-batch-prompt"
              value={promptTemplate}
              onChange={(e) => setPromptTemplate(e.target.value)}
              placeholder='e.g. "Generate hero image for all chapters. Make each image unique based on {chapter_title} and {chapter_description}. Editorial illustration, soft palette."'
              rows={3}
              maxLength={800}
              disabled={state.phase === "running"}
              className="mt-1 w-full border border-[color:var(--border)] px-3 py-2 text-sm focus:outline-none focus:border-[color:var(--primary)]"
              data-testid="hero-batch-prompt-template"
            />
            <p className="text-[11px] text-[color:var(--text-muted)] mt-1">
              Variables: <code>{"{chapter_title}"}</code>, <code>{"{chapter_subtitle}"}</code>, <code>{"{chapter_description}"}</code>.
              Saved on the book so you can return and re-run later.
            </p>
          </div>
        )}

        {/* Progress bar */}
        {(state.phase === "running" || state.phase === "done" || state.phase === "cancelled") && (
          <div className="px-6 pt-4">
            <div className="h-2 bg-[color:var(--canvas)] border border-[color:var(--border)] overflow-hidden">
              <div
                className={`h-full transition-all duration-300 ${errorCount ? "bg-amber-500" : "bg-[color:var(--primary)]"}`}
                style={{ width: `${pct}%` }}
                data-testid="hero-batch-progressbar"
                data-progress-pct={pct}
              />
            </div>
            <p className="text-[11px] uppercase tracking-widest text-[color:var(--text-muted)] mt-1">
              {pct}% · {doneCount}/{totalCount} generated{errorCount ? ` · ${errorCount} failed` : ""}
            </p>
          </div>
        )}

        {quotaHit && (
          <div className="mx-6 mt-3 border border-amber-400 bg-amber-50 text-amber-900 px-3 py-2 text-sm flex items-start gap-2"
               role="alert" data-testid="hero-batch-quota-banner">
            <AlertCircle className="w-4 h-4 mt-0.5 shrink-0"/>
            <div className="flex-1 min-w-0">
              <p className="font-medium">Nano Banana quota exceeded during batch</p>
              <p className="text-xs mt-0.5">
                Remaining chapters will keep failing until you top up the Universal Key.
              </p>
              <a href="/settings/emergent-key" className="text-xs underline mt-1 inline-block"
                 data-testid="hero-batch-quota-link">
                Open Profile → Universal Key
              </a>
            </div>
          </div>
        )}

        {/* Row list */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          {(state.rows.length ? state.rows : targets.map((t) => ({ ...t, status: "pending", error: null }))).map((r) => (
            <div
              key={r.source_index}
              className="flex items-center gap-3 py-2 border-b border-[color:var(--border)] last:border-b-0"
              data-testid={`hero-batch-row-${r.source_index}`}
              data-status={r.status}
            >
              <div className="w-5 h-5 shrink-0 flex items-center justify-center">
                {r.status === "done" && <CheckCircle2 className="w-4 h-4 text-emerald-600"/>}
                {r.status === "error" && <XCircle className="w-4 h-4 text-red-600"/>}
                {r.status === "generating" && <Loader2 className="w-4 h-4 text-[color:var(--primary)] animate-spin"/>}
                {r.status === "pending" && <span className="w-2 h-2 rounded-full bg-[color:var(--border)]"/>}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm truncate">{r.title}</p>
                {r.error && <p className="text-[11px] text-red-600 truncate">{r.error}</p>}
              </div>
              <div className="text-[11px] uppercase tracking-widest text-[color:var(--text-muted)] shrink-0">
                {r.status}
              </div>
            </div>
          ))}
          {!targets.length && (
            <div className="text-sm text-center py-6" data-testid="hero-batch-empty">
              <p className="text-[color:var(--text-muted)]">
                Every chapter already has a hero image.
              </p>
              <p className="text-[11px] text-[color:var(--text-muted)] mt-1">
                You can still edit the shared prompt template above and save
                it for the next re-run — remove a chapter's hero from its
                panel and re-open this batch to regenerate.
              </p>
            </div>
          )}
        </div>

        {/* Footer actions */}
        <div className="border-t border-[color:var(--border)] px-6 py-3 flex items-center justify-end gap-2">
          {state.phase === "idle" && targets.length > 0 && (
            <button
              onClick={start}
              className="btn-primary text-sm inline-flex items-center gap-2"
              data-testid="hero-batch-start"
            >
              <Sparkles className="w-4 h-4"/> Generate {targets.length} hero image{targets.length === 1 ? "" : "s"}
            </button>
          )}
          {state.phase === "idle" && !targets.length && (
            <button
              onClick={savePromptTemplate}
              className="btn-primary text-sm inline-flex items-center gap-2"
              disabled={savingTemplate}
              data-testid="hero-batch-save-template"
            >
              {savingTemplate ? "Saving…" : "Save prompt for later"}
            </button>
          )}
          {state.phase === "running" && (
            <button onClick={abort} className="btn-ghost text-sm" data-testid="hero-batch-abort">
              Stop
            </button>
          )}
          {(state.phase === "done" || state.phase === "cancelled") && (
            <button onClick={onClose} className="btn-primary text-sm" data-testid="hero-batch-done-close">
              Close
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
